import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";

import { formatNumber } from "utils/format";
import Loading from "components/Loading";
import Panel from "components/Panel";
import { useRoutes } from "utils/routes";

import {
  DatabaseSummaryStatsTable_getSummaryStatsByDatabase,
  DatabaseSummaryStatsTable,
  DatabaseSummaryStatsTableVariables,
} from "./types/DatabaseSummaryStatsTable";
import QUERY from "./Query.graphql";
import { StatusDotIcon } from "components/Icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons";
import Grid, { GridColumn } from "components/Grid";
import { useQueryStringSearch } from "components/QueryStringSearch";

type RowDataType = {
  serverId: string;
  serverName: string;
  status: string;
  databaseId: string;
  databaseName: string;
  slowQueries: number;
  unusedIndexes: number;
  warnings: number;
  criticals: number;
};

type Props = {
  organizationSlug?: string;
  serverId?: string;
};

const Table: React.FunctionComponent<Props> = ({
  organizationSlug,
  serverId,
}) => {
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const [showInactive, setShowInactive] = useState(false);
  const { data, loading, error } = useQuery<
    DatabaseSummaryStatsTable,
    DatabaseSummaryStatsTableVariables
  >(QUERY, {
    variables: {
      organizationSlug,
      serverId,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  let databases: Array<RowDataType> = data.getSummaryStatsByDatabase.map(
    (
      summary: DatabaseSummaryStatsTable_getSummaryStatsByDatabase,
    ): RowDataType => {
      return {
        serverId: summary.database.server.humanId,
        serverName: summary.database.server.name,
        databaseId: summary.database.id,
        databaseName: summary.database.datname,
        status: summary.database.server.hasRecentSnapshot
          ? summary.database.server.hasCriticalIssues
            ? "critical"
            : "okay"
          : "no data",
        slowQueries: summary.queriesSlownessCount,
        unusedIndexes: summary.unusedIndexesCount,
        warnings: summary.warningsCount,
        criticals: summary.criticalsCount,
      };
    },
  );

  if (searchTerm) {
    databases = databases.filter(
      (di: RowDataType): boolean =>
        di.serverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        di.databaseName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }

  if (!serverId && !showInactive) {
    databases = databases.filter((d) => d.status != "no data");
  }

  const titleSearch = (
    <>
      {serverId ? null : (
        <div className="flex">
          <input
            type="checkbox"
            checked={showInactive}
            id="show_inactive"
            onChange={(evt) => setShowInactive(evt.target.checked)}
          />
          <label htmlFor="show_inactive">
            Show servers with no recent statistics data
          </label>
        </div>
      )}
      <SearchInput />
    </>
  );

  const columns: GridColumn<
    (typeof databases)[number],
    keyof (typeof databases)[number]
  >[] = [
    {
      field: "status",
      header: "",
      width: !serverId ? "24px" : false,
      renderer: function StatusCell({ fieldData }) {
        return <StatusDotIcon status={fieldData} />;
      },
    },
    {
      field: "serverName",
      header: "Server",
      width: !serverId ? "minmax(150px,1fr)" : false,
      renderer: ServerNameCell,
    },
    {
      field: "databaseName",
      header: "Database",
      width: "minmax(150px,1fr)",
      renderer: DatabaseNameCell,
    },
    {
      field: "slowQueries",
      header: "Slow Queries",
      width: !serverId ? "minmax(10%,110px)" : "minmax(15%,110px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: NumericCell,
    },
    {
      field: "unusedIndexes",
      header: "Unused Indexes",
      width: !serverId ? "minmax(10%,110px)" : "minmax(15%,110px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: NumericCell,
    },
    {
      field: "warnings",
      header: "Warnings",
      width: !serverId ? "minmax(10%,110px)" : "minmax(15%,110px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: NumericCell,
    },
    {
      field: "criticals",
      header: "Criticals",
      width: !serverId ? "minmax(10%,110px)" : "minmax(15%,110px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: NumericCell,
    },
  ];

  return (
    <Panel title="All Databases" secondaryTitle={titleSearch}>
      <Grid
        data={databases}
        defaultSortBy={serverId ? "databaseName" : "serverName"}
        striped
        columns={columns}
      />
    </Panel>
  );
};

const ServerNameCell: React.FunctionComponent<{ rowData: RowDataType }> = ({
  rowData,
}) => {
  const { server } = useRoutes();
  return <Link to={server(rowData.serverId)}>{rowData.serverName}</Link>;
};

const DatabaseNameCell: React.FunctionComponent<{ rowData: RowDataType }> = ({
  rowData,
}) => {
  const { database } = useRoutes();
  return (
    <>
      <Link to={database(rowData.databaseId)}>{rowData.databaseName}</Link>
      {rowData.status != "no data" && rowData.criticals > 0 ? (
        <FontAwesomeIcon
          icon={faExclamationCircle}
          className="text-[#ff0000] ml-[5px]"
        />
      ) : null}
    </>
  );
};

const NumericCell: React.FunctionComponent<{ fieldData: number }> = ({
  fieldData,
}) => {
  return <>{formatNumber(fieldData)}</>;
};

export default Table;
